import React, { useState, useEffect } from 'react';
import { useFont } from '../../Design/FontContext';
import { getButtonEffectStyles } from '../utils/buttonEffectsUtils';
import './ProductFormPopup.css';
import './WidgetStyle.css';
import DeleteModal from './DeleteModal';

function appendPx(value) {
    if (typeof value === 'string' && isNaN(value)) {
        return value;
    }
    return `${value}px`;
}

// Standalone Popup Component (for use with ProductInfoWidget)
export const ProductFormPopupStandalone = ({ 
    isOpen, 
    onClose, 
    productData, 
    customStyling = {},
    isPreviewMode = false,
    onSubmit 
}) => {
    const [formData, setFormData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const fonts = useFont();

    // Reset form when popup opens
    useEffect(() => {
        if (isOpen) {
            setFormData({});
            setIsSubmitting(false);
            setIsClosing(false);
        }
    }, [isOpen]);

    // Handle close with animation
    const handleClose = () => {
        setIsClosing(true);
        // Wait for animation to complete before actually closing
        setTimeout(() => {
            onClose();
        }, 400); // Match animation duration
    };

    // Handle form field changes
    const handleInputChange = (fieldName, value) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: value
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isPreviewMode) {
            alert('Preview Mode: Form would be submitted with data: ' + JSON.stringify(formData, null, 2));
            return;
        }

        setIsSubmitting(true);
        
        try {
            await onSubmit(formData);
            onClose();
        } catch (error) {
            console.error('Form submission error:', error);
            alert('Error submitting form. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Get form fields from product data
    const getFormFields = () => {
        if (!productData?.formFields) {
            // Default fields if none configured
            return [
                { name: 'name', label: 'Full Name', type: 'text', required: true },
                { name: 'email', label: 'Email Address', type: 'email', required: true }
            ];
        }
        return productData.formFields;
    };

    // Render form field based on type
    const renderFormField = (field) => {
        const commonProps = {
            id: field.name,
            name: field.name,
            required: field.required,
            placeholder: field.placeholder || field.label,
            value: formData[field.name] || '',
            onChange: (e) => handleInputChange(field.name, e.target.value),
            style: {
                width: '100%',
                padding: '16px 20px',
                border: `2px solid ${customStyling.fieldBorderColor || '#e5e7eb'}`,
                borderRadius: '12px',
                fontSize: '16px',
                fontFamily: fonts.globalFont || 'Arial, sans-serif',
                backgroundColor: customStyling.fieldBackgroundColor || '#ffffff',
                color: customStyling.fieldTextColor || '#1f2937',
                transition: 'all 0.3s ease',
                outline: 'none'
            }
        };

        switch (field.type) {
            case 'textarea':
                return (
                    <textarea
                        {...commonProps}
                        rows={4}
                        style={{
                            ...commonProps.style,
                            resize: 'vertical',
                            minHeight: '120px'
                        }}
                    />
                );
            case 'select':
                return (
                    <select {...commonProps}>
                        <option value="">Select {field.label}</option>
                        {field.options?.map((option, index) => (
                            <option key={index} value={option.value || option}>
                                {option.label || option}
                            </option>
                        ))}
                    </select>
                );
            default:
                return <input {...commonProps} type={field.type || 'text'} />;
        }
    };

    if (!isOpen) return null;

    return (
        <div className={`product-form-fullpage pages-product-popup-animation ${isClosing ? 'closing' : ''}`} style={{
            position: 'fixed',
            top: isPreviewMode ? '60px' : 0,
            left: isPreviewMode ? '270px' : 0,
            right: isPreviewMode ? '270px' : 0,
            bottom: '-60px',
            backgroundColor: customStyling.overlayColor || 'rgba(0, 0, 0, 0.75)',
            backdropFilter: customStyling.overlayBlur ? `blur(${customStyling.overlayBlur}px)` : 'none',
            zIndex: 9999,
            overflow: 'auto',
            padding: '40px 20px'
        }}>
            {/* Preview Mode Indicator - Links oben am Hintergrund */}
            {isPreviewMode && (
                <div style={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                    zIndex: 10
                }}>
                    🧪 PREVIEW
                </div>
            )}

            {/* Close Button - Rechts oben am Hintergrund */}
            <button 
                onClick={handleClose}
                type="button"
                aria-label="Close"
                style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    width: '40px',
                    height: '40px',
                    border: 'none',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: '#374151',
                    transition: 'all 0.3s ease',
                    zIndex: 10,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
                onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.9)';
                    e.target.style.color = '#ffffff';
                    e.target.style.transform = 'scale(1.1)';
                    e.target.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.4)';
                }}
                onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                    e.target.style.color = '#374151';
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
            >
                ×
            </button>
            <div className="product-form-container" style={{
                maxWidth: `${customStyling.modalWidth || 600}px`,
                margin: '0 auto',
                width: '100%'
            }}>
                <div className="product-form-content" style={{
                    background: customStyling.backgroundColor || '#ffffff',
                    borderRadius: `${customStyling.borderRadius || 20}px`,
                    paddingTop: `${customStyling.paddingTop || 5}px`,
                    paddingLeft: `${customStyling.padding || 25}px`,
                    paddingRight: `${customStyling.padding || 25}px`,
                    paddingBottom: `${customStyling.padding || 25}px`,
                    boxShadow: customStyling.boxShadow === 'none' ? 'none' :
                               customStyling.stylingMode === 'transparent' ? 'none' : 
                               customStyling.stylingMode === 'glass' ? '0 8px 32px rgba(31, 38, 135, 0.37)' :
                               customStyling.stylingMode === 'seamless' ? 'none' :
                               '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    position: 'relative',
                    backdropFilter: customStyling.stylingMode === 'glass' ? 'blur(10px)' : 'none',
                    border: customStyling.stylingMode === 'glass' ? '1px solid rgba(255, 255, 255, 0.18)' : 'none'
                }}>

                    {/* Header */}
                    <div className="product-form-popup-header">
                        {/* Header Image/Logo */}
                        {customStyling.headerImage && (
                            <div style={{
                                textAlign: 'center',
                                marginBottom: '24px',
                                marginTop: '20px' // 20px Abstand nach oben
                            }}>
                                <img 
                                    src={customStyling.headerImage}
                                    alt="Logo"
                                    style={{
                                        width: `${customStyling.headerImageSize || 80}px`,
                                        height: 'auto',
                                        borderRadius: customStyling.headerImageShape === 'circle' ? '50%' :
                                                     customStyling.headerImageShape === 'square' ? '0px' : '8px',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                        transition: 'all 0.3s ease',
                                        objectFit: customStyling.headerImageShape === 'circle' ? 'cover' : 'contain',
                                        aspectRatio: customStyling.headerImageShape === 'circle' ? '1' : 'auto'
                                    }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        )}
                        
                        <h2 style={{ 
                            fontFamily: fonts.globalFont || 'Arial, sans-serif',
                            fontSize: `${customStyling.headerTitleSize || 28}px`,
                            fontWeight: '700',
                            color: customStyling.headerTitleColor || '#1f2937',
                            margin: customStyling.headerImage ? '0 0 8px 0' : '20px 0 8px 0', // 20px Abstand nach oben wenn kein Image
                            textAlign: 'center'
                        }}>
                            {customStyling.headerTitle || 'Get Free Access'}
                        </h2>
                        
                        {/* Custom Subtitle - only show if text is provided */}
                        {customStyling.headerSubtitle && (
                            <p style={{
                                fontFamily: fonts.globalFont || 'Arial, sans-serif',
                                fontSize: `${customStyling.headerSubtitleSize || 16}px`,
                                color: customStyling.headerSubtitleColor || '#6b7280',
                                margin: '0 0 32px 0',
                                textAlign: 'center',
                                lineHeight: '1.5'
                            }}>
                                {customStyling.headerSubtitle}
                            </p>
                        )}
                        
                        {/* Default Product-based subtitle - only show if no custom subtitle */}
                        {!customStyling.headerSubtitle && (
                            <p style={{
                                fontFamily: fonts.globalFont || 'Arial, sans-serif',
                                fontSize: `${customStyling.headerSubtitleSize || 16}px`,
                                color: customStyling.headerSubtitleColor || '#6b7280',
                                margin: '0 0 32px 0',
                                textAlign: 'center',
                                lineHeight: '1.5'
                            }}>
                                {productData?.title ? `Get instant access to "${productData.title}"` : 'Please fill out the form below to get instant access'}
                            </p>
                        )}
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="product-form-popup-form">
                        {getFormFields().map((field, index) => (
                            <div key={index} className="product-form-popup-field">
                                <label 
                                    htmlFor={field.name}
                                    style={{
                                        display: 'block',
                                        fontFamily: fonts.globalFont || 'Arial, sans-serif',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: customStyling.labelColor || '#374151',
                                        marginBottom: '8px'
                                    }}
                                >
                                    {field.label}
                                    {field.required && <span style={{ color: '#ef4444' }}>*</span>}
                                </label>
                                {renderFormField(field)}
                            </div>
                        ))}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="product-form-popup-submit"
                            style={{
                                width: '100%',
                                padding: '18px 24px',
                                backgroundColor: customStyling.submitButtonColor || '#3b82f6',
                                color: customStyling.submitButtonTextColor || 'white',
                                border: 'none',
                                borderRadius: '12px',
                                fontSize: '16px',
                                fontWeight: '600',
                                fontFamily: fonts.globalFont || 'Arial, sans-serif',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                transition: 'all 0.3s ease',
                                opacity: isSubmitting ? 0.7 : 1,
                                marginTop: '24px',
                                ...getButtonEffectStyles(customStyling.buttonEffect || 'none')
                            }}
                        >
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}>
                                <span>{isSubmitting ? 'Submitting...' : (customStyling.buttonText || 'Get Free Access Now')}</span>
                                {customStyling.buttonIcon && !isSubmitting && (
                                    <img 
                                        src={`/img/${customStyling.buttonIcon}`}
                                        alt="Button Icon"
                                        style={{
                                            width: '16px',
                                            height: '16px',
                                            filter: customStyling.buttonIconInvert ? 'invert(1)' : 'none',
                                            transition: 'all 0.3s ease'
                                        }}
                                    />
                                )}
                            </div>
                        </button>
                    </form>

                    {/* Footer */}
                    <div className="product-form-popup-footer">
                        <p style={{
                            fontFamily: fonts.globalFont || 'Arial, sans-serif',
                            fontSize: '12px',
                            color: customStyling.footerTextColor || '#9ca3af',
                            textAlign: 'center',
                            margin: '24px 0 0 0',
                            lineHeight: '1.4'
                        }}>
                            {customStyling.footerText || '🔒 Your information is secure and will never be shared.'}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Main Widget Component (for use in page builder)
const ProductFormPopup = ({ data, isSelected, onRemove, isEditMode }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showTestPopup, setShowTestPopup] = useState(false);
    const fonts = useFont();
    const deleteLogo = '/img/delete.svg';

    // Widget styling
    function isDark(color) {
        const rgb = parseInt(color.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >>  8) & 0xff;
        const b = (rgb >>  0) & 0xff;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        return luma < 128;
    }

    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                const borderColor = isDark(data.backgroundColor || '#ffffff') ? '#FFFFFF' : '#000000';
                return `2px dashed ${borderColor}`;
            } else {
                return data.border ? `1.5px solid ${data.borderColor}` : '2px dashed transparent';
            }
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : 'none';
        }
    }

    const widgetStyle = {
        backgroundColor: data.backgroundColor || '#ffffff',
        marginBottom: appendPx(data.marginBottom || 0),
        marginTop: appendPx(data.marginTop || 0),
        border: getBorderStyle(),
        borderRadius: appendPx(data.borderRadius || 0),
        padding: '24px',
        position: 'relative',
        minHeight: '120px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: isEditMode ? 'pointer' : 'default'
    };


    // Listen for test form popup events
    useEffect(() => {
        const handleTestFormPopup = (event) => {
            if (isEditMode) {
                setShowTestPopup(true);
            }
        };

        window.addEventListener('testFormPopup', handleTestFormPopup);
        
        return () => {
            window.removeEventListener('testFormPopup', handleTestFormPopup);
        };
    }, [isEditMode]);

    // Handle test form submission
    const handleTestFormSubmit = async (formData) => {
        console.log('Test form submitted:', formData);
        alert(`Preview Mode: Form would be submitted with:\n${JSON.stringify(formData, null, 2)}`);
    };

    // Handle delete
    const handleDelete = () => {
        onRemove();
        setShowDeleteModal(false);
    };

    return (
        <div className="product-form-popup-widget" style={widgetStyle}>
            {/* Delete Button */}
            {isEditMode && (
                <div>
                    <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                        <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                    </button>
                    {showDeleteModal && (
                        <DeleteModal
                            isOpen={showDeleteModal}
                            onClose={() => setShowDeleteModal(false)}
                            onConfirm={handleDelete}
                        />
                    )}
                </div>
            )}

            {/* Widget Content */}
            <div style={{ textAlign: 'center' }}>
                <div style={{
                    fontSize: '24px',
                    marginBottom: '8px'
                }}>
                    📝
                </div>
                <h3 style={{
                    fontFamily: fonts.globalFont || 'Arial, sans-serif',
                    fontSize: data.titleSize || '18px',
                    fontWeight: '700',
                    color: data.titleColor || '#1f2937',
                    margin: '0 0 8px 0'
                }}>
                    {data.title || 'Product Form Popup'}
                </h3>
                <p style={{
                    fontFamily: fonts.globalFont || 'Arial, sans-serif',
                    fontSize: data.descriptionSize || '14px',
                    color: data.descriptionColor || '#6b7280',
                    margin: '0 0 16px 0'
                }}>
                    {data.description || 'Collect user information for free digital products'}
                </p>
                
                {/* Info Text */}
                <div style={{
                    fontSize: '14px',
                    color: data.descriptionColor || '#6b7280',
                    textAlign: 'center',
                    fontStyle: 'italic'
                }}>
                    {isEditMode ? 'Configure form fields and styling in the sidebar →' : 'Form will be triggered by product buttons'}
                </div>
            </div>

            {/* Test Popup */}
            <ProductFormPopupStandalone
                isOpen={showTestPopup}
                onClose={() => setShowTestPopup(false)}
                productData={{
                    title: data.productTitle || 'Sample Digital Product',
                    formFields: data.formFields || [
                        { name: 'name', label: 'Full Name', type: 'text', required: true, placeholder: 'Enter your full name' },
                        { name: 'email', label: 'Email Address', type: 'email', required: true, placeholder: 'Enter your email address' }
                    ]
                }}
                customStyling={{
                    backgroundColor: data.backgroundColor || '#ffffff',
                    titleColor: data.titleColor || '#1f2937',
                    buttonColor: data.buttonColor || '#3b82f6',
                    buttonTextColor: data.buttonTextColor || '#ffffff'
                }}
                isPreviewMode={true}
                onSubmit={handleTestFormSubmit}
            />
        </div>
    );
};

export default ProductFormPopup;
